/**
 * トップのクリエイター一覧に、スクロールに連れた視差を付ける
 *
 * @description 札ごと列単位で上下へ流す。写真は枠の中で動かさない。
 *              PCは3列の真ん中だけ、SPは2列の右だけを逆向きに流す。
 *
 *              測るのは .creators__listItem で、動かすのはその中の .Creatorcard。
 *              動かす相手をそのまま測ると、自分が書いた変形が次のフレームに混ざる。
 *              市松のずらしも listItem がCSSで持っているので触らずに済む
 */

/** PCの基準幅。流す量を画面幅に連れて変える */
const PC_WIDTH = 1728;

/**
 * PCで札を上下へ流す量（PC基準px）。片道ぶん
 *
 * @description 真ん中の列は逆向きに流すので、両端との差は最大でこの2倍動く。
 *              CSSで下げてある76より小さく保たないと、画面の上のほうで真ん中が両端より上がって見える
 */
const PC_SHIFT = 30;

/** SPの基準幅 */
const SP_WIDTH = 393;

/** SPで札を上下へ流す量（SP基準px）。片道ぶん */
const SP_SHIFT = 20;

/**
 * 背景の上端を丸める量。背景の幅に対する割合で持つ
 *
 * @description 横半径は幅の半分でSCSSが固定しているので、ここで渡すのは弧の高さだけ。
 *              固定pxだと画面が広いほど弧が浅く見えるので幅に対する割合で持つ
 */
const BG_ARC_RATIO = 0.25;

/** 監視を止める手続き。非同期遷移で一覧ごと作り直すときに使う */
let detach: (() => void) | null = null;

/** 背景の角丸の監視を止める手続き */
let detachBg: (() => void) | null = null;

/**
 * 0〜1に丸める
 *
 * @param v 元の値
 * @returns 0〜1の値
 */
const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

/**
 * 動きを止めて、書いた指定を消す
 *
 * @returns {void}
 */
const destroyCreatorsParallax = (): void => {
    detach?.();
    detach = null;
};

/**
 * 視差を始める
 *
 * @returns {void}
 */
const initCreatorsParallax = (): void => {
    destroyCreatorsParallax();

    const list = document.querySelector<HTMLElement>('.creators__list');

    if (!list) return;

    // 装飾なので、モーションを抑える設定では動かさない
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const isSp = window.matchMedia('(max-width: 1024px)').matches;

    const pairs = [...list.querySelectorAll<HTMLElement>('.creators__listItem')]
        .map((el) => ({ el, target: el.querySelector<HTMLElement>('.Creatorcard') }))
        .filter((p): p is { el: HTMLElement; target: HTMLElement } => p.target !== null);

    if (pairs.length === 0) return;

    let raf = 0;

    /**
     * いまのスクロール位置に合わせて置き直す
     *
     * @returns {void}
     */
    const update = (): void => {
        const h = Math.max(1, window.innerHeight);
        const cols = isSp ? 2 : 3;
        const shift = isSp
            ? SP_SHIFT * (window.innerWidth / SP_WIDTH)
            : PC_SHIFT * (window.innerWidth / PC_WIDTH);

        pairs.forEach(({ el, target }, i) => {
            const r = el.getBoundingClientRect();

            // 札が画面の下から入って上へ抜けるまでで -1→1。画面の中央で0
            const q = clamp01((h - r.top) / (h + r.height)) * 2 - 1;

            // CSSで下げてある列（PCは真ん中、SPは右）だけ逆向きに流す
            const dir = i % cols === 1 ? -1 : 1;

            target.style.transform = `translate3d(0, ${(dir * shift * q).toFixed(1)}px, 0)`;
        });
    };

    const tick = (): void => {
        raf = requestAnimationFrame(tick);
        update();
    };

    const start = (): void => {
        if (raf) return;

        raf = requestAnimationFrame(tick);
    };

    const stop = (): void => {
        cancelAnimationFrame(raf);
        raf = 0;
    };

    update();

    // 節が画面に無いあいだは毎フレームの書き換えを止める
    const io = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
            start();
        } else {
            stop();
        }
    }, { threshold: 0 });

    io.observe(list);

    detach = () => {
        stop();
        io.disconnect();
        pairs.forEach(({ target }) => {
            target.style.transform = '';
        });
    };
};

/**
 * 背景の角丸を止めて、指定を消す
 *
 * @returns {void}
 */
const destroyCreatorsBg = (): void => {
    detachBg?.();
    detachBg = null;
};

/**
 * 背景の上端の丸みをスクロールに追わせる
 *
 * @description 節が画面の下から上がってくるあいだは上端が丸く、上端へ着いたところで角になる。
 *              奥行きの動きとは別に持つ。あちらはモーションを抑える設定では丸ごと止まるが、
 *              こちらは止めたままだと丸みが残り続けてしまう。
 * @returns {void}
 */
const initCreatorsBg = (): void => {
    destroyCreatorsBg();

    const section = document.querySelector<HTMLElement>('.creators');
    const bg = section?.querySelector<HTMLElement>('.creators__bg');

    if (!section || !bg) return;

    // モーションを抑える設定でも動かす。弧はスクロールに連れて形が決まるもので、
    // 止めると平らなまま固定されてデザインの形そのものが失われる。
    // Windowsは性能目的でアニメーション効果を切っている機がそこそこあり、
    // そこだけ角ばって見えていた
    let raf = 0;

    /**
     * いまの位置から丸みを決める
     *
     * @returns {void}
     */
    const update = (): void => {
        raf = 0;

        // 節の上端が画面の中央より下にある間は丸いまま。そこから画面の上端までで角になる
        const top = section.getBoundingClientRect().top;
        const reach = Math.max(1, window.innerHeight / 2);
        const p = clamp01(top / reach);

        const arc = bg.clientWidth * BG_ARC_RATIO * p;

        bg.style.setProperty('--creators-bg-arc', `${arc.toFixed(1)}px`);
    };

    const onScroll = (): void => {
        if (raf) return;

        raf = requestAnimationFrame(update);
    };

    update();

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    detachBg = () => {
        if (raf) cancelAnimationFrame(raf);

        raf = 0;
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
        bg.style.removeProperty('--creators-bg-arc');
    };
};

export { initCreatorsParallax, destroyCreatorsParallax, initCreatorsBg, destroyCreatorsBg };

document.addEventListener('DOMContentLoaded', () => {
    initCreatorsParallax();
    initCreatorsBg();
});
